/**
 * Abbonamento: che piano ho, quanto ne ho consumato, come cambiarlo.
 *
 * La regola dell'interfaccia è la stessa del backend: prima di far scendere di
 * piano si dice cosa succederà, in chiaro e con i numeri. Scoprirlo dopo è il
 * modo migliore per farsi disdire l'abbonamento.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  CreditCard, Check, Loader2, AlertTriangle, Ticket, ArrowUpRight, ArrowDownRight, Clock, ExternalLink,
} from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { billingApi, type Plan, type DowngradeImpact } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

function gb(bytes: number): string {
  if (bytes <= 0) return '0';                                   // "0 KB" suona sbagliato
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / 1024 / 1024)} MB`;
  const g = bytes / 1024 / 1024 / 1024;
  const arrotondato = g >= 10 ? String(Math.round(g)) : g.toFixed(1).replace(/\.0$/, '');
  return `${arrotondato} GB`;        // "20 GB", non "20.0 GB"
}

/** Quanto viene al mese un piano annuale: è il confronto che fa chiunque. */
function euroMese(annuo: number): string {
  const m = (Math.round((annuo / 12) * 100) / 100).toFixed(2).replace('.', ',');
  return `€${m.endsWith(',00') ? m.slice(0, -3) : m}`;
}

function Barra({ label, used, limit, format: fmt, capienza }: {
  label: string; used: number; limit: number;
  format?: (n: number) => string;
  /** Capienza (caselle), non consumo: essere pieni è normale, non un guasto. */
  capienza?: boolean;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const f = fmt ?? ((n: number) => String(n));
  // Il colore avvisa prima che sia troppo tardi, non quando è già finita.
  // Ma un piano Solo sta a 1/1 per sempre: tingerlo di rosso a vita è un
  // allarme che non chiede niente a nessuno e insegna a ignorare il rosso.
  const tono =
    used > limit ? 'bg-rose-500'
    : capienza ? 'bg-indigo-500'
    : pct >= 100 ? 'bg-rose-500'
    : pct >= 80 ? 'bg-amber-500'
    : 'bg-indigo-500';

  return (
    <div>
      {/* Il valore non deve mai andare a capo: spezzare "0 / 20 GB" su due
          righe rende illeggibile proprio il numero che si sta cercando. */}
      <div className="flex items-baseline justify-between gap-3 text-sm mb-1.5">
        <span className="min-w-0">{label}</span>
        <span className="text-muted-foreground whitespace-nowrap shrink-0">
          {f(used)} <span className="opacity-60">/ {f(limit)}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', tono)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function BillingPage() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [target, setTarget] = useState<Plan | null>(null);
  const [impact, setImpact] = useState<DowngradeImpact | null>(null);

  const meQuery = useQuery({ queryKey: ['billing-me'], queryFn: () => billingApi.me() });
  const plansQuery = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans() });

  const me = meQuery.data;
  const plans = plansQuery.data?.plans ?? [];

  const changeMut = useMutation({
    mutationFn: (plan: string) => billingApi.changePlan(plan),
    onSuccess: (r) => {
      setTarget(null); setImpact(null);
      qc.invalidateQueries({ queryKey: ['billing-me'] });
      toast.success(
        r.applied
          ? 'Piano aggiornato: le funzioni nuove sono già attive.'
          : `Cambio prenotato: sarà attivo dal ${r.effectiveAt ? format(new Date(r.effectiveAt), 'd MMMM', { locale: it }) : 'fine periodo'}. Fino ad allora resta tutto com'è.`
      );
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Cambio non riuscito'),
  });

  const redeemMut = useMutation({
    mutationFn: () => billingApi.redeem(code.trim()),
    onSuccess: () => {
      setCode('');
      qc.invalidateQueries({ queryKey: ['billing-me'] });
      toast.success('Codice applicato');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Codice non valido'),
  });

  const pagamentiAttivi = plansQuery.data?.paymentsEnabled ?? false;
  /** Chi non ha ancora un abbonamento pagato passa dal checkout Stripe. */
  const daAttivare = !!me && (me.trialing || me.inGrace || me.status === 'trial_ended');

  /** Mensile o annuale. La scelta vale solo alla prima attivazione: chi ha già
   *  un abbonamento cambia periodicità dal pannello Stripe, dove ci sono anche
   *  i conguagli da fare. */
  const [annuale, setAnnuale] = useState(false);

  const checkoutMut = useMutation({
    mutationFn: (plan: string) => billingApi.checkout(plan, annuale),
    onSuccess: (r) => { window.location.href = r.url; },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco ad aprire il pagamento'),
  });

  const portalMut = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (r) => { window.location.href = r.url; },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco ad aprire la gestione'),
  });

  /** Chi non ha ancora pagato non sta cambiando piano: lo sta scegliendo. */
  const sceglie = pagamentiAttivi && daAttivare;
  /** Giorni di prova ancora davanti: sotto i due, Stripe addebita subito. */
  const giorniProva = me?.trialEndsAt
    ? Math.floor((new Date(me.trialEndsAt).getTime() - Date.now()) / 86_400_000)
    : 0;

  /** Prima di scendere si chiede cosa comporta e lo si mostra. */
  async function scegli(p: Plan) {
    if (!me) return;
    // Non si ricompra il piano che si ha già. Ma in prova il piano "attuale" è
    // solo il valore di partenza della riga: lì il cliente non sta ricomprando
    // niente, sta scegliendo. Senza questa distinzione il bottone del piano di
    // partenza non fa nulla, in silenzio.
    if (!sceglie && p.id === me.planId) return;

    // Nessun abbonamento pagato ancora: si passa dal checkout, non dal
    // cambio interno, altrimenti si regalerebbe il piano.
    if (pagamentiAttivi && daAttivare) {
      checkoutMut.mutate(p.id);
      return;
    }

    const scende = plans.findIndex((x) => x.id === p.id) < plans.findIndex((x) => x.id === me.planId);
    if (!scende) {
      changeMut.mutate(p.id);
      return;
    }
    try {
      setTarget(p);
      setImpact(await billingApi.impact(p.id));
    } catch {
      setTarget(null);
      toast.error('Non riesco a calcolare cosa comporta il cambio. Riprova.');
    }
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-8 space-y-7">

          <header className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
              <CreditCard className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">Abbonamento</h1>
              <p className="text-sm text-muted-foreground mt-1">Il tuo piano e quanto ne hai usato questo mese.</p>
            </div>
          </header>

          {meQuery.isLoading || !me ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Carico…
            </div>
          ) : (
            <>
              {/* stato attuale */}
              <div className={cn(
                'rounded-2xl p-6 text-white',
                me.inGrace ? 'bg-gradient-to-br from-amber-600 to-orange-600' : 'bg-gradient-to-br from-indigo-600 to-violet-600'
              )}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm opacity-85">{me.trialing ? 'In prova' : me.inGrace ? 'In sospeso' : 'Piano attivo'}</div>
                    <div className="text-2xl font-bold mt-0.5">{me.plan.label}</div>
                    {me.trialing && me.trialEndsAt && (
                      <div className="text-sm opacity-90 mt-1.5">
                        La prova finisce il {format(new Date(me.trialEndsAt), 'd MMMM', { locale: it })}
                      </div>
                    )}
                    {me.inGrace && (
                      <div className="text-sm opacity-95 mt-1.5 max-w-sm">
                        Puoi leggere la posta e scaricare i file, ma non collegare caselle nuove.
                        Scegli un piano per riattivare tutto.
                      </div>
                    )}
                  </div>
                  {!me.trialing && !me.inGrace && (
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold">€{me.plan.price}</div>
                      <div className="text-xs opacity-80">al mese</div>
                    </div>
                  )}
                </div>

                {/* Chi già paga gestisce carta, fatture e disdetta su Stripe:
                    rifarlo qui vorrebbe dire riscrivere anche i solleciti. */}
                {pagamentiAttivi && !daAttivare && (
                  <div className="mt-5">
                    <Button
                      variant="secondary" size="sm"
                      disabled={portalMut.isPending}
                      onClick={() => portalMut.mutate()}
                    >
                      {portalMut.isPending
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <ExternalLink className="h-4 w-4 mr-2" />}
                      Metodo di pagamento e fatture
                    </Button>
                  </div>
                )}
              </div>

              {/* consumi */}
              <div className="rounded-xl border p-5 space-y-4">
                <h2 className="font-semibold text-sm">Questo mese</h2>
                <Barra label="Caselle collegate" used={me.usage.mailboxes.used} limit={me.usage.mailboxes.limit} capienza />
                <Barra label="Risposte automatiche della Reception" used={me.usage.receptionReplies.used} limit={me.usage.receptionReplies.limit} />
                <Barra label="Minuti di trascrizione" used={me.usage.transcriptionMinutes.used} limit={me.usage.transcriptionMinutes.limit} />
                <Barra label="Spazio per i file inviati" used={me.usage.storage.usedBytes} limit={me.usage.storage.limitBytes} format={gb} />
                <p className="text-xs text-muted-foreground pt-1">
                  I contatori si azzerano il primo del mese. Superata la soglia le funzioni automatiche si mettono
                  in pausa, ma quello che avvii tu continua a funzionare.
                </p>
              </div>

              {/* piani */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h2 className="font-semibold text-sm">{sceglie ? 'Scegli il piano' : 'Cambia piano'}</h2>
                  {sceglie && (
                    <div className="inline-flex rounded-lg border p-0.5 text-xs shrink-0">
                      <button
                        type="button" onClick={() => setAnnuale(false)}
                        className={cn('px-2.5 py-1 rounded-md transition-colors',
                          annuale ? 'text-muted-foreground hover:text-foreground' : 'bg-indigo-600 text-white')}
                      >
                        Mensile
                      </button>
                      <button
                        type="button" onClick={() => setAnnuale(true)}
                        className={cn('px-2.5 py-1 rounded-md transition-colors',
                          annuale ? 'bg-indigo-600 text-white' : 'text-muted-foreground hover:text-foreground')}
                      >
                        Annuale · 2 mesi gratis
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {plans.map((p) => {
                    const attuale = p.id === me.planId && !me.trialing;
                    return (
                      <div key={p.id} className={cn(
                        'rounded-xl border p-4 flex flex-col',
                        attuale && 'border-indigo-500 bg-indigo-50/40'
                      )}>
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-semibold leading-7">{p.label}</span>
                          <div className="text-right shrink-0">
                            <div className="text-lg font-bold leading-7">
                              €{annuale && sceglie ? p.priceYearly : p.price}
                              <span className="text-xs font-normal text-muted-foreground">
                                /{annuale && sceglie ? 'anno' : 'mese'}
                              </span>
                            </div>
                            {annuale && sceglie && (
                              <div className="text-[11px] text-muted-foreground -mt-0.5">
                                {euroMese(p.priceYearly)} al mese
                              </div>
                            )}
                          </div>
                        </div>
                        <ul className="mt-3 space-y-1 text-sm text-muted-foreground flex-1">
                          <li>{p.mailboxes} {p.mailboxes === 1 ? 'casella' : 'caselle'}</li>
                          <li>{p.receptionReplies} risposte Reception</li>
                          <li>{Math.round(p.transcriptionMinutes / 60)} h di verbali · {p.storageGb} GB</li>
                          {p.receptionAuto && <li className="text-indigo-600 font-medium">Reception autonoma</li>}
                        </ul>
                        {attuale ? (
                          <div className="mt-3 text-sm font-medium text-indigo-600 flex items-center gap-1.5">
                            <Check className="h-4 w-4" /> Piano attuale
                          </div>
                        ) : (
                          <Button
                            variant="outline" size="sm" className="mt-3"
                            disabled={changeMut.isPending || checkoutMut.isPending}
                            onClick={() => void scegli(p)}
                          >
                            {sceglie
                              ? <>Scegli {p.label}</>
                              : plans.findIndex((x) => x.id === p.id) > plans.findIndex((x) => x.id === me.planId)
                                ? <><ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Passa a {p.label}</>
                                : <><ArrowDownRight className="h-3.5 w-3.5 mr-1" /> Scendi a {p.label}</>}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {sceglie
                    ? giorniProva >= 2
                      ? `Non paghi oggi: l'addebito parte ${me.trialEndsAt ? `il ${format(new Date(me.trialEndsAt), 'd MMMM', { locale: it })}` : 'a fine prova'}, quando finisce la prova. Se disdici prima non paghi niente.`
                      : "L'abbonamento parte subito e si rinnova da solo. Puoi disdirlo quando vuoi."
                    : "Salire vale subito. Scendere ha effetto a fine periodo pagato: fino ad allora resta tutto com'è, e puoi ripensarci."}
                </p>
              </div>

              {/* codice invito */}
              <div className="rounded-xl border p-5">
                <h2 className="font-semibold text-sm flex items-center gap-1.5"><Ticket className="h-4 w-4" /> Hai un codice?</h2>
                <p className="text-sm text-muted-foreground mt-1">Se te ne è stato dato uno, inseriscilo qui.</p>
                <div className="flex gap-2 mt-3">
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX-XXXX"
                    className="font-mono"
                  />
                  <Button onClick={() => redeemMut.mutate()} disabled={code.trim().length < 4 || redeemMut.isPending}>
                    {redeemMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Applica'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* conferma della discesa, con le conseguenze in chiaro */}
      {target && impact && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold">Scendere a {target.label}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Ecco cosa cambia. Niente viene cancellato.</p>
              </div>
            </div>

            <ul className="mt-4 space-y-2 text-sm">
              {impact.mailboxesOver > 0 && (
                <li className="flex gap-2">
                  <Clock className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>
                    <strong>{impact.mailboxesOver} {impact.mailboxesOver === 1 ? 'casella verrà messa' : 'caselle verranno messe'} in pausa.</strong>{' '}
                    Smettono di aggiornarsi, ma la posta resta leggibile e tornano attive se risali di piano.
                  </span>
                </li>
              )}
              {impact.losesReceptionAuto && (
                <li className="flex gap-2"><span className="text-amber-600">•</span>
                  <span>La Reception non risponderà più da sola: preparerà le risposte da approvare.</span></li>
              )}
              {impact.losesDelivery && (
                <li className="flex gap-2"><span className="text-amber-600">•</span>
                  <span>L'invio curato e il controllo del dominio non saranno più inclusi.</span></li>
              )}
              {impact.losesSharedMailboxes && (
                <li className="flex gap-2"><span className="text-amber-600">•</span>
                  <span>Le caselle condivise coi collaboratori non saranno più incluse.</span></li>
              )}
              {impact.storageOver > 0 && (
                <li className="flex gap-2"><span className="text-amber-600">•</span>
                  <span>Sei oltre lo spazio di {gb(impact.storageOver)}: non potrai inviare file nuovi finché non scadono i link esistenti.</span></li>
              )}
              {impact.mailboxesOver === 0 && !impact.losesReceptionAuto && !impact.losesDelivery
                && !impact.losesSharedMailboxes && impact.storageOver === 0 && (
                <li className="text-muted-foreground">Nessuna conseguenza: quello che usi rientra già nel piano più piccolo.</li>
              )}
            </ul>

            <p className="text-xs text-muted-foreground mt-4">
              Il cambio avrà effetto a fine periodo pagato. Fino ad allora puoi annullarlo.
            </p>

            <div className="flex gap-2 mt-5">
              <Button variant="outline" className="flex-1" onClick={() => { setTarget(null); setImpact(null); }}>
                Lascia stare
              </Button>
              <Button className="flex-1" disabled={changeMut.isPending} onClick={() => changeMut.mutate(target.id)}>
                {changeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confermo'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
